/**
 * Cone of Depression Tab
 *
 * 1. Single well cross-section with Theis cone at computed GPM
 * 2. Full wellfield cross-section with overlapping cones
 * 3. Top-down drawdown map with ROI circles
 *
 * Per-well pump rate is derived from the well's uploaded DTW gauging history
 * via inverse Theis (Cooper-Jacob). If the well has < 6 readings or < 2 yrs
 * of span, we fall back to the manual `pump_rate_gpm` on the asset record.
 * Wells with neither sufficient gauging nor a manual rate are excluded from
 * the cone calculations entirely — no synthetic load, no double-deduction
 * against V5 inference.
 */
import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { MapContainer, Circle, CircleMarker, Rectangle, Tooltip as LTooltip } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { apiClient } from '../../api/client';
import { EsriVectorBasemap } from '@/lib/basemap';
import {
  AQUIFER_K,
  AQUIFER_S,
  theisDrawdown,
  deriveWellGpm,
  manualPerWellGpm,
  MIN_GAUGING_READINGS,
  MIN_GAUGING_SPAN_DAYS,
  type GpmSource,
  type ManualDemand,
} from '@/lib/wellGpm';
import { ManualDemandConfig } from './ManualDemandConfig';

interface Well {
  well_id: string;
  well_name: string;
  latitude: number;
  longitude: number;
  total_depth_ft: number;
  depth_to_water_ft: number;
  ground_elevation_ft: number;
  /** Effective sustained Q for Theis modeling. Derived from gauging when
   *  available; otherwise the manual asset value; otherwise 0 (excluded). */
  pump_rate_gpm: number;
  /** Where the effective Q came from. Surface in the UI. */
  q_source: GpmSource;
  q_n_readings: number;
  q_span_days: number;
}

interface ConeOfDepressionTabProps {
  siteId: string;
  totalGpm: number;
  pumpHrs: number;
  aquifer: string;
  centerLat: number;
  centerLon: number;
  /** Shared manual-demand fallback (use case + quantity + wells) for sites
   *  with no gauging. Per-well GPM derived from it feeds Theis when a well has
   *  neither a gauging-derived nor a manual asset rate. */
  manualDemand: ManualDemand;
  onManualDemandChange: (next: ManualDemand) => void;
}

const SVG_W = 800;
const SVG_H = 350;
const MARGIN = { top: 30, right: 30, bottom: 40, left: 60 };

// Pan/zoom limits for the wellfield cross-section — match DV CrossSectionChart.
const ZOOM_MIN = 0.5;
const ZOOM_MAX = 4;
const ZOOM_STEP = 0.15;

// Compute the "natural" transect bearing (degrees from north) from the widest
// well-spread. Mirrors landing/staging Data Vault CrossSectionChart.tsx so the
// two charts pick the same default axis for the same well set.
function computeNaturalBearing(wells: { latitude: number; longitude: number }[]): number {
  if (wells.length < 2) return 0;
  let maxDist = 0;
  let a = 0, b = 1;
  for (let i = 0; i < wells.length; i++) {
    for (let j = i + 1; j < wells.length; j++) {
      const dLat = (wells[i].latitude - wells[j].latitude) * 364000;
      const dLon = (wells[i].longitude - wells[j].longitude) * 308000;
      const d = Math.sqrt(dLat * dLat + dLon * dLon);
      if (d > maxDist) { maxDist = d; a = i; b = j; }
    }
  }
  const dLat = wells[b].latitude - wells[a].latitude;
  const dLon = wells[b].longitude - wells[a].longitude;
  // atan2(dLat, dLon) returns angle from east (math convention). Convert to
  // bearing from north (clockwise, 0–360).
  let bearing = (90 - Math.atan2(dLat, dLon) * (180 / Math.PI)) % 360;
  if (bearing < 0) bearing += 360;
  return bearing;
}

// Compass widget — shows current transect bearing relative to true north.
// Identical visual to DV CrossSectionChart so the two charts feel like one
// system. Static SVG; pure presentation.
function NorthCompass({ bearingDeg }: { bearingDeg: number }) {
  return (
    <div className="flex flex-col items-center gap-1 select-none">
      <svg width={56} height={56} viewBox="0 0 56 56">
        <circle cx={28} cy={28} r={26} fill="rgba(0,0,0,0.35)" stroke="#374151" strokeWidth={1.5} />
        {[0, 45, 90, 135, 180, 225, 270, 315].map(deg => {
          const rad = (deg - bearingDeg) * (Math.PI / 180) - Math.PI / 2;
          return (
            <line
              key={deg}
              x1={28 + Math.cos(rad) * 22}
              y1={28 + Math.sin(rad) * 22}
              x2={28 + Math.cos(rad) * 26}
              y2={28 + Math.sin(rad) * 26}
              stroke="#9ca3af"
              strokeWidth={deg % 90 === 0 ? 2 : 1}
              opacity={deg % 90 === 0 ? 0.8 : 0.4}
            />
          );
        })}
        <g transform={`rotate(${-bearingDeg}, 28, 28)`}>
          <polygon points="28,4 24,20 32,20" fill="#ef4444" opacity={0.9} />
          <polygon points="28,52 24,36 32,36" fill="#9ca3af" opacity={0.4} />
          <text x={28} y={14} textAnchor="middle" fontSize={7} fontWeight={700} fill="#fff">N</text>
        </g>
        {/* Transect line indicator — horizontal = the slice direction */}
        <line x1={4} y1={28} x2={52} y2={28} stroke="#22d3ee" strokeWidth={1.5} opacity={0.5} strokeDasharray="3 2" />
        <circle cx={28} cy={28} r={2} fill="#22d3ee" />
      </svg>
      <div className="text-[11px] font-mono tabular-nums tracking-wider text-cyan-400">
        {Math.round(bearingDeg)}°
      </div>
    </div>
  );
}

// Nice-number tick generator (Wilkinson-style). Returns rounded tick values
// covering [min, max] with about `targetCount` ticks at human-readable steps.
function niceTicks(min: number, max: number, targetCount: number): number[] {
  if (max <= min) return [min];
  const range = max - min;
  const rawStep = range / Math.max(targetCount, 1);
  const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)));
  const stepCandidates = [1, 2, 2.5, 5, 10];
  const stepNorm = rawStep / magnitude;
  const stepMul = stepCandidates.find(s => s >= stepNorm) ?? 10;
  const step = stepMul * magnitude;
  const start = Math.ceil(min / step) * step;
  const ticks: number[] = [];
  // Guard against floating-point drift on the upper bound
  for (let t = start; t <= max + step * 1e-9; t += step) {
    ticks.push(Math.round(t * 1e6) / 1e6);
  }
  return ticks;
}

export function ConeOfDepressionTab({ siteId, totalGpm, pumpHrs, aquifer, centerLat, centerLon, manualDemand, onManualDemandChange }: ConeOfDepressionTabProps) {
  // Raw assets + gaugings, fetched once per site. Q is derived in a memo below
  // so changing the manual-demand fallback re-derives without refetching.
  const [rawWells, setRawWells] = useState<Array<{ asset: any; gaugings: Array<{ date: string | null; depth_to_water_ft: number | null }> }>>([]);
  const [loading, setLoading] = useState(true);
  const [selectedWell, setSelectedWell] = useState(0);

  // Per-well GPM from the manual fallback (0 when not configured).
  const manualWellGpm = manualPerWellGpm(manualDemand);

  // Load PumpAssets (site wells only — not regional observation wells).
  // For each asset, fetch its gauging history and derive an effective sustained
  // Q via Theis inversion. If gauging is too sparse, fall back to the manual
  // pump_rate_gpm on the asset. If both are missing, the well contributes 0 to
  // the cone (excluded — no synthetic load).
  const K = AQUIFER_K[aquifer] || 25;
  const S = AQUIFER_S[aquifer] || 0.17;
  const t_days = pumpHrs / 24;

  const loadWells = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await apiClient.get(`/phreatic/pump-control/assets/${siteId}?status=active`);
      const assets: any[] = resp.data.assets || [];

      const enriched = await Promise.all(
        assets.map(async (a: any) => {
          let gaugings: Array<{ date: string | null; depth_to_water_ft: number | null }> = [];
          try {
            const g = await apiClient.get(`/phreatic/pump-control/assets/${a.id}/gaugings`);
            gaugings = g.data.gaugings || [];
          } catch {
            // Gauging fetch failed — deriveWellGpm falls through to manual.
          }
          return { asset: a, gaugings };
        }),
      );
      setRawWells(enriched);
    } catch {
      setRawWells([]);
    } finally {
      setLoading(false);
    }
  }, [siteId]);

  useEffect(() => { loadWells(); }, [loadWells]);

  // Derive each well's effective Q. Priority (in deriveWellGpm): gauging-derived
  // > manual asset rate > manual use-case fallback > none. Re-runs when the
  // aquifer or the manual fallback changes — no refetch needed.
  const wells: Well[] = useMemo(() => rawWells.map(({ asset: a, gaugings }) => {
    const q = deriveWellGpm(a, gaugings, aquifer, manualWellGpm);
    return {
      well_id: a.id,
      well_name: a.well_name,
      latitude: a.latitude,
      longitude: a.longitude,
      total_depth_ft: Number(a.well_depth_ft) || 300,
      depth_to_water_ft: Number(a.current_dtw_ft) || 0,
      ground_elevation_ft: Number(a.ground_elevation_ft) || 0,
      pump_rate_gpm: q.gpm,
      q_source: q.source,
      q_n_readings: q.n_readings,
      q_span_days: q.span_days,
    } as Well;
  }), [rawWells, aquifer, manualWellGpm]);

  // Aggregated metric for headers/labels. `totalGpm` prop is intentionally
  // ignored now — Cone of Depression is driven by per-well derived/manual Q
  // so it doesn't double-count against V5 inference (which already embeds
  // the wells' historical pumping signature in the gauging-trained forecast).
  const totalDerivedGpm = wells.reduce((s, w) => s + (w.pump_rate_gpm || 0), 0);
  const wellsWithQ = wells.filter((w) => w.pump_rate_gpm > 0);

  // Single well cone data — symmetric ±r profile with log-spaced sampling
  // so the steep gradient near the well is captured cleanly.
  const singleCone = useMemo(() => {
    const w = wells[selectedWell];
    if (!w || w.pump_rate_gpm <= 0) return null;
    const wellQ = w.pump_rate_gpm;
    const satFt = Math.max(0, w.total_depth_ft - w.depth_to_water_ft);
    if (satFt <= 0) return null;

    // 1) Find the radius of influence — first r where drawdown drops below
    //    0.1 ft. Dense linear scan to capture the boundary precisely.
    let roi = 0;
    for (let r = 1; r <= 20000; r += r < 100 ? 5 : r < 1000 ? 25 : 100) {
      const dd = theisDrawdown(wellQ, satFt, K, S, r, t_days);
      if (dd < 0.1) {
        roi = r;
        break;
      }
    }
    if (roi === 0) roi = 5000; // bail-out: cone never decays within the scan

    // 2) Visual x-domain. Use the ROI itself as the half-width — the curve
    //    decays to ~0.1 ft drawdown there by definition, so the cone fills
    //    the chart edge-to-edge with no wasted blank margin.
    const xMax = Math.max(roi, 100);

    // 3) Log-spaced sampling from ~0.5 ft (effective well radius) out to xMax.
    //    Log spacing puts more points where the curve is steepest.
    const numSamples = 80;
    const rMin = 0.5;
    const logMin = Math.log10(rMin);
    const logMax = Math.log10(xMax);
    const points: { r: number; dd: number }[] = [];
    for (let i = 0; i <= numSamples; i++) {
      const t = i / numSamples;
      const r = Math.pow(10, logMin + t * (logMax - logMin));
      const ddRaw = theisDrawdown(wellQ, satFt, K, S, r, t_days);
      // Clip at saturated thickness — drawdown can't physically exceed it
      // (the well would dewater). This is a hard physical limit, not a
      // rendering hack.
      points.push({ r, dd: Math.min(ddRaw, satFt) });
    }

    const maxDd = Math.max(...points.map(p => p.dd));
    return { well: w, satFt, points, roi, xMax, maxDd };
  }, [wells, selectedWell, K, S, t_days]);

  // ── Wellfield transect: bearing / zoom / pan state ────────────────────
  // Mirrors Data Vault CrossSectionChart so the two cross-sections feel like
  // one system: left-drag rotates the transect axis, scroll zooms in/out,
  // middle-or-right-drag pans, "FIT" resets.
  const wellfieldContainerRef = useRef<HTMLDivElement>(null);
  const naturalBearing = useMemo(() => computeNaturalBearing(wells), [wells]);
  const [bearingOverride, setBearingOverride] = useState<number | null>(null);
  const activeBearing = bearingOverride ?? naturalBearing;
  const [wfZoom, setWfZoom] = useState(1);
  const [wfPanX, setWfPanX] = useState(0);
  const [wfPanY, setWfPanY] = useState(0);
  const [wfIsPanning, setWfIsPanning] = useState(false);
  const [wfIsRotating, setWfIsRotating] = useState(false);
  const wfPanStart = useRef({ x: 0, y: 0, panX: 0, panY: 0 });
  const wfRotateStart = useRef({ clientX: 0, clientY: 0, startBearing: 0 });

  // Full wellfield — project wells onto the active transect bearing. Same
  // visual logic as the single-well cone (blue water body, depression carved
  // out, auto-scaled Y) but spread across all wells along the transect.
  // Only wells with a positive per-well Q (derived or manual) contribute.
  const wellfieldCone = useMemo(() => {
    const contributingWells = wells.filter((w) => w.pump_rate_gpm > 0);
    if (contributingWells.length < 2) return null;

    // Centroid as projection origin so rotating the bearing doesn't shift
    // the chart sideways — wells just orbit around a fixed midpoint.
    const refLat = contributingWells.reduce((s, w) => s + w.latitude, 0) / contributingWells.length;
    const refLon = contributingWells.reduce((s, w) => s + w.longitude, 0) / contributingWells.length;

    // Axis unit vector for the bearing. Bearing is degrees clockwise from
    // north (north = +lat, east = +lon).
    const rad = activeBearing * (Math.PI / 180);
    const axLatComponent = Math.cos(rad);
    const axLonComponent = Math.sin(rad);

    const projected = contributingWells.map(w => {
      const dLatFt = (w.latitude - refLat) * 364000;
      const dLonFt = (w.longitude - refLon) * 308000;
      const pos = dLatFt * axLatComponent + dLonFt * axLonComponent;
      const satFt = Math.max(0, w.total_depth_ft - w.depth_to_water_ft);
      return { ...w, pos, satFt };
    }).sort((a, b) => a.pos - b.pos);

    const minPos = projected[0].pos;
    const maxPos = projected[projected.length - 1].pos;
    const span = Math.max(maxPos - minPos, 100);
    const padding = Math.max(span * 0.2, 500); // breathing room past edges
    const xMin = minPos - padding;
    const xMax = maxPos + padding;

    // Max sat thickness across all wells — physical cap on combined drawdown
    // (no well can produce more drawdown than its saturated thickness, so the
    // combined sum can't physically exceed the deepest well's sat).
    const maxSat = Math.max(...projected.map(p => p.satFt));

    // Dense sampling so the combined cone (which has steep peaks at each
    // well location) renders smoothly.
    const numSamples = 240;
    const transectPoints: { x: number; combinedDd: number }[] = [];
    for (let i = 0; i <= numSamples; i++) {
      const x = xMin + (i / numSamples) * (xMax - xMin);
      let totalDd = 0;
      for (const w of projected) {
        const dist = Math.abs(x - w.pos);
        const r = Math.max(dist, 0.5);
        totalDd += theisDrawdown(w.pump_rate_gpm, w.satFt, K, S, r, t_days);
      }
      transectPoints.push({ x, combinedDd: Math.min(totalDd, maxSat) });
    }

    const avgWtElev = projected.reduce(
      (s, w) => s + (w.ground_elevation_ft - w.depth_to_water_ft), 0,
    ) / projected.length;
    const maxDd = Math.max(...transectPoints.map(p => p.combinedDd));

    return {
      projected,
      transectPoints,
      xMin,
      xMax,
      span: xMax - xMin,
      avgWtElev,
      maxDd,
    };
  }, [wells, activeBearing, K, S, t_days]);

  // ── Wellfield interaction handlers ─────────────────────────────────────

  const wfHandleWheel = useCallback((e: React.WheelEvent) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP;
    setWfZoom(z => {
      const next = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, z + delta));
      // Zoom toward the cursor — same pivot math as DV CrossSectionChart.
      if (wellfieldContainerRef.current) {
        const rect = wellfieldContainerRef.current.getBoundingClientRect();
        const cx = e.clientX - rect.left;
        const cy = e.clientY - rect.top;
        const scale = next / z;
        setWfPanX(px => cx - scale * (cx - px));
        setWfPanY(py => cy - scale * (cy - py));
      }
      return next;
    });
  }, []);

  const wfHandlePointerDown = useCallback((e: React.PointerEvent) => {
    (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
    if (e.button === 0) {
      // Left drag = rotate the transect bearing
      setWfIsRotating(true);
      wfRotateStart.current = { clientX: e.clientX, clientY: e.clientY, startBearing: activeBearing };
    } else if (e.button === 1 || e.button === 2) {
      // Middle / right drag = pan the chart
      setWfIsPanning(true);
      wfPanStart.current = { x: e.clientX, y: e.clientY, panX: wfPanX, panY: wfPanY };
    }
  }, [wfPanX, wfPanY, activeBearing]);

  const wfHandlePointerMove = useCallback((e: React.PointerEvent) => {
    if (wfIsRotating) {
      const dx = e.clientX - wfRotateStart.current.clientX;
      let newBearing = (wfRotateStart.current.startBearing - dx * 0.5) % 360;
      if (newBearing < 0) newBearing += 360;
      setBearingOverride(newBearing);
    }
    if (wfIsPanning) {
      setWfPanX(wfPanStart.current.panX + (e.clientX - wfPanStart.current.x));
      setWfPanY(wfPanStart.current.panY + (e.clientY - wfPanStart.current.y));
    }
  }, [wfIsRotating, wfIsPanning]);

  const wfHandlePointerUp = useCallback(() => {
    setWfIsRotating(false);
    setWfIsPanning(false);
  }, []);

  const wfHandleResetView = useCallback(() => {
    setWfZoom(1);
    setWfPanX(0);
    setWfPanY(0);
    setBearingOverride(null);
  }, []);

  // ROI per well for map — uses each well's own derived/manual Q. Wells with
  // Q == 0 (no usable gauging, no manual rate) get roi_ft = 0 and are excluded
  // from the ROI circles downstream.
  const wellROIs = useMemo(() => {
    return wells.map(w => {
      if (w.pump_rate_gpm <= 0) return { ...w, roi_ft: 0, roi_m: 0 };
      const satFt = Math.max(0, w.total_depth_ft - w.depth_to_water_ft);
      for (let r = 100; r <= 10000; r += 50) {
        if (theisDrawdown(w.pump_rate_gpm, satFt, K, S, r, t_days) < 0.1) {
          return { ...w, roi_ft: r, roi_m: r * 0.3048 };
        }
      }
      return { ...w, roi_ft: 10000, roi_m: 3048 };
    });
  }, [wells, K, S, t_days]);

  if (loading) {
    return (
      <div className="bg-mono-900 border border-mono-800 rounded-lg p-6 text-center">
        <p className="text-mono-400 text-sm animate-pulse">Loading site wells...</p>
      </div>
    );
  }

  if (wells.length === 0) {
    return (
      <div className="bg-mono-900 border border-mono-800 rounded-lg p-6 text-center">
        <p className="text-mono-400 text-sm">No site wells configured.</p>
        <p className="text-mono-500 text-xs mt-1">Add your production wells via the Well Map (click to place) or Well Intelligence (AI recommendations) to see cone of depression analysis.</p>
      </div>
    );
  }

  // Data gate: each well's Q comes from its own gauging history (Theis
  // inversion) or its manual pump_rate_gpm asset value. If no well has either,
  // there's nothing physically meaningful to plot — bail out with a clear
  // message instead of rendering a flat-zero cone.
  if (wellsWithQ.length === 0) {
    return (
      <div className="space-y-3">
        <ManualDemandConfig config={manualDemand} onChange={onManualDemandChange} />
        <div className="bg-mono-900 border border-mono-800 rounded-lg p-6 text-center">
        <p className="text-mono-300 text-sm font-semibold mb-2 uppercase tracking-wider">
          No Pump-Rate Data
        </p>
        <p className="text-mono-500 text-xs leading-relaxed max-w-md mx-auto">
          The cone of depression is driven by each well's own pump rate. Upload
          DTW gauging history (≥ {MIN_GAUGING_READINGS} readings over ≥{' '}
          {Math.round(MIN_GAUGING_SPAN_DAYS / 365)} years) via Well Inventory
          to derive Q from the observed decline trend, or enter a manual{' '}
          <span className="text-mono-300">pump_rate_gpm</span> on each well
          asset, or set a manual use case above. Currently no well has either.
        </p>
        </div>
      </div>
    );
  }

  const plotW = SVG_W - MARGIN.left - MARGIN.right;
  const plotH = SVG_H - MARGIN.top - MARGIN.bottom;

  return (
    <div className="space-y-3">
      {/* Manual demand fallback — applies to any well lacking gauging/manual Q */}
      <ManualDemandConfig config={manualDemand} onChange={onManualDemandChange} />

      {/* ── Single Well Cross-Section ── */}
      <div className="bg-mono-900 border border-mono-800 rounded-lg p-4">
        <h3 className="text-sm font-semibold text-mono-200 uppercase tracking-wider mb-3">
          Single Well — Cone of Depression
        </h3>
        {singleCone && (() => {
          const { points, roi, xMax, maxDd, satFt, well } = singleCone;
          // Y domain: 10% headroom above peak drawdown so the curve doesn't
          // graze the top axis. Minimum 0.5 ft so we never render a degenerate
          // axis when drawdown is near zero everywhere.
          const yMax = Math.max(maxDd * 1.1, 0.5);

          // Pixel-coordinate mappers. x is centered on the well (r=0); y grows
          // downward in screen space because drawdown convention puts 0 at top.
          const pixelX = (xData: number) =>
            MARGIN.left + ((xData + xMax) / (2 * xMax)) * plotW;
          const pixelY = (dd: number) =>
            MARGIN.top + (dd / yMax) * plotH;

          // Build the path. Walk the left side from far-to-near (negative r),
          // then the right side from near-to-far (positive r). The result is a
          // single continuous symmetric curve: gentle slope at the far edges,
          // steep dive at the well center, ascending back out the other side.
          const sortedAsc = [...points].sort((a, b) => a.r - b.r);
          const symmetric: { x: number; dd: number }[] = [];
          for (let i = sortedAsc.length - 1; i >= 0; i--) {
            symmetric.push({ x: -sortedAsc[i].r, dd: sortedAsc[i].dd });
          }
          for (let i = 0; i < sortedAsc.length; i++) {
            symmetric.push({ x: sortedAsc[i].r, dd: sortedAsc[i].dd });
          }

          const linePath = symmetric
            .map((p, i) => `${i === 0 ? 'M' : 'L'} ${pixelX(p.x).toFixed(2)} ${pixelY(p.dd).toFixed(2)}`)
            .join(' ');

          // Closed fill: water table (top of plot) → down along the curve →
          // back across the top to close. Produces the filled cone-of-water
          // look the Talon PG reference uses.
          const fillPath =
            `M ${pixelX(-xMax).toFixed(2)} ${pixelY(0).toFixed(2)} ` +
            symmetric
              .map(p => `L ${pixelX(p.x).toFixed(2)} ${pixelY(p.dd).toFixed(2)}`)
              .join(' ') +
            ` L ${pixelX(xMax).toFixed(2)} ${pixelY(0).toFixed(2)} Z`;

          const xTicks = niceTicks(-xMax, xMax, 8);
          const yTicks = niceTicks(0, yMax, 5);
          const T = K * satFt;

          return (
            <>
              <div className="text-[10px] text-mono-500 mb-2">
                {well.well_name} | {well.pump_rate_gpm.toFixed(0)} GPM
                {well.q_source === 'derived' && (
                  <span className="text-cyan-400/80">
                    {' '}
                    (derived from {well.q_n_readings} readings, {(well.q_span_days / 365).toFixed(1)} yr span)
                  </span>
                )}
                {well.q_source === 'manual' && (
                  <span className="text-mono-400">{' '}(manual)</span>
                )}
                {well.q_source === 'manual_use_case' && (
                  <span className="text-mono-400">{' '}(manual use case)</span>
                )}
                {' '}| {pumpHrs}hr/day |
                Sat: {satFt.toFixed(0)}ft | Max drawdown: {maxDd.toFixed(1)}ft | ROI: {roi.toLocaleString()}ft
              </div>
              <div className="relative">
                {/* Well selector overlaid on the chart — switching wells
                    recomputes the cone and redraws the SVG. */}
                <select
                  value={selectedWell}
                  onChange={e => setSelectedWell(Number(e.target.value))}
                  className="absolute top-2 right-2 z-10 px-2 py-1 text-xs bg-mono-800/90 backdrop-blur border border-mono-700 rounded text-mono-200 focus:outline-none focus:border-cyan-500/50"
                >
                  {wells.map((w, i) => <option key={i} value={i}>{w.well_name}</option>)}
                </select>
              <svg viewBox={`0 0 ${SVG_W} ${SVG_H}`} className="w-full bg-mono-950 rounded">
                {/* Axis lines */}
                <line x1={MARGIN.left} y1={MARGIN.top} x2={MARGIN.left} y2={SVG_H - MARGIN.bottom} stroke="#555" />
                <line x1={MARGIN.left} y1={SVG_H - MARGIN.bottom} x2={SVG_W - MARGIN.right} y2={SVG_H - MARGIN.bottom} stroke="#555" />
                <text x={SVG_W / 2} y={SVG_H - 5} fill="#666" fontSize="10" textAnchor="middle">
                  r (ft from well)
                </text>
                <text x={15} y={SVG_H / 2} fill="#666" fontSize="10" textAnchor="middle"
                      transform={`rotate(-90, 15, ${SVG_H / 2})`}>
                  Drawdown s(r,t) (ft)
                </text>

                {/* Auto-scaled Y axis */}
                {yTicks.map(v => {
                  const y = pixelY(v);
                  return (
                    <g key={`y${v}`}>
                      <line x1={MARGIN.left - 5} y1={y} x2={MARGIN.left} y2={y} stroke="#555" />
                      <text x={MARGIN.left - 8} y={y + 3} fill="#888" fontSize="9" textAnchor="end">
                        {v.toFixed(v >= 10 ? 0 : v >= 1 ? 1 : 2)}
                      </text>
                      <line x1={MARGIN.left} y1={y} x2={SVG_W - MARGIN.right} y2={y}
                            stroke="#333" strokeDasharray="3 3" />
                    </g>
                  );
                })}

                {/* Symmetric X axis — ticks include negative values left of the well */}
                {xTicks.map(v => {
                  const x = pixelX(v);
                  return (
                    <g key={`x${v}`}>
                      <line x1={x} y1={SVG_H - MARGIN.bottom} x2={x} y2={SVG_H - MARGIN.bottom + 5} stroke="#555" />
                      <text x={x} y={SVG_H - MARGIN.bottom + 15} fill="#888" fontSize="9" textAnchor="middle">
                        {v.toLocaleString()}
                      </text>
                      {v !== 0 && (
                        <line x1={x} y1={MARGIN.top} x2={x} y2={SVG_H - MARGIN.bottom}
                              stroke="#333" strokeDasharray="3 3" />
                      )}
                    </g>
                  );
                })}

                {/* Saturated aquifer — solid blue body filling everything below
                    the water table. The cone-shaped depression on top of this
                    will get carved out by a dark overlay below. */}
                <rect
                  x={MARGIN.left}
                  y={pixelY(0)}
                  width={plotW}
                  height={SVG_H - MARGIN.bottom - pixelY(0)}
                  fill="#06b6d4"
                  fillOpacity={0.55}
                />

                {/* Cone depression — dewatered void carved out of the water body.
                    Filled near-black so it reads as empty space against the
                    blue aquifer, matching the Talon PG reference (where the
                    depression appears as the printed white). */}
                <path d={fillPath} fill="#0a0a0a" />

                {/* Curve outline — boundary between the saturated body and the
                    dewatered depression. */}
                <path d={linePath} stroke="#22d3ee" fill="none" strokeWidth={2} />

                {/* Water table (drawdown = 0) — drawn last so it sits on top
                    of both fills. */}
                <line x1={MARGIN.left} y1={pixelY(0)} x2={SVG_W - MARGIN.right} y2={pixelY(0)}
                      stroke="#60a5fa" strokeWidth={2} />
                <text x={MARGIN.left + 5} y={pixelY(0) - 5} fill="#60a5fa" fontSize="9">Water Table</text>

                {/* Well casing at r = 0 — the actual well asset. Drawn brighter
                    than before so it's easy to find as the dropdown changes. */}
                <line x1={pixelX(0)} y1={MARGIN.top} x2={pixelX(0)} y2={SVG_H - MARGIN.bottom}
                      stroke="#e5e7eb" strokeWidth={1.5} opacity={0.85} />
                <text x={pixelX(0)} y={MARGIN.top - 5} fill="#e5e7eb" fontSize="9" textAnchor="middle">
                  {well.well_name}
                </text>

                {/* Theis model annotation box (matches Talon PG reference style) */}
                <g>
                  <rect x={MARGIN.left + 8} y={MARGIN.top + 8} width={130} height={42}
                        fill="#0a0a0a" stroke="#444" strokeWidth={0.5} opacity={0.85} />
                  <text x={MARGIN.left + 14} y={MARGIN.top + 22} fill="#bbb" fontSize="9">Theis (1935) Model</text>
                  <text x={MARGIN.left + 14} y={MARGIN.top + 34} fill="#888" fontSize="8">
                    T = {T.toFixed(0)} ft²/d
                  </text>
                  <text x={MARGIN.left + 14} y={MARGIN.top + 44} fill="#888" fontSize="8">
                    S = {S} · t = {t_days.toFixed(2)} d
                  </text>
                </g>
              </svg>
              </div>
            </>
          );
        })()}
      </div>

      {/* ── Full Wellfield Cross-Section ── */}
      {wellfieldCone && (() => {
        const { projected, transectPoints, xMin, xMax: wfXMax, span, maxDd: wfMaxDd } = wellfieldCone;

        // Auto-scaled Y identical to the single-well chart.
        const yMax = Math.max(wfMaxDd * 1.1, 0.5);

        // Pixel mappers
        const pixelX = (xData: number) => MARGIN.left + ((xData - xMin) / span) * plotW;
        const pixelY = (dd: number) => MARGIN.top + (dd / yMax) * plotH;

        // Build the combined-depression path. Same shape as the single-well
        // fill — water-table top corners → curve → water-table top corners.
        const linePath = transectPoints
          .map((p, i) => `${i === 0 ? 'M' : 'L'} ${pixelX(p.x).toFixed(2)} ${pixelY(p.combinedDd).toFixed(2)}`)
          .join(' ');

        const fillPath =
          `M ${pixelX(xMin).toFixed(2)} ${pixelY(0).toFixed(2)} ` +
          transectPoints
            .map(p => `L ${pixelX(p.x).toFixed(2)} ${pixelY(p.combinedDd).toFixed(2)}`)
            .join(' ') +
          ` L ${pixelX(wfXMax).toFixed(2)} ${pixelY(0).toFixed(2)} Z`;

        // Axis ticks. X is "distance along transect" centered roughly on the
        // wellfield centroid (which is at projection origin x=0 for the
        // active bearing).
        const xTicks = niceTicks(xMin, wfXMax, 8);
        const yTicks = niceTicks(0, yMax, 5);

        return (
          <div className="bg-mono-900 border border-mono-800 rounded-lg p-4">
            {/* Header: title + compass + zoom controls */}
            <div className="flex items-start justify-between mb-3 flex-wrap gap-3">
              <h3 className="text-sm font-semibold text-mono-200 uppercase tracking-wider">
                Wellfield Cross-Section — Combined Drawdown
              </h3>
              <NorthCompass bearingDeg={activeBearing} />
              <div className="flex items-center gap-1.5">
                <span className="text-[10px] text-mono-500 mr-1">{Math.round(wfZoom * 100)}%</span>
                <button
                  onClick={() => setWfZoom(z => Math.max(ZOOM_MIN, z - ZOOM_STEP * 2))}
                  className="w-6 h-6 flex items-center justify-center rounded border border-mono-700 text-xs text-mono-400 hover:bg-mono-800"
                >−</button>
                <button
                  onClick={() => setWfZoom(z => Math.min(ZOOM_MAX, z + ZOOM_STEP * 2))}
                  className="w-6 h-6 flex items-center justify-center rounded border border-mono-700 text-xs text-mono-400 hover:bg-mono-800"
                >+</button>
                <button
                  onClick={wfHandleResetView}
                  className="h-6 px-2 flex items-center justify-center rounded border border-mono-700 text-[10px] text-mono-400 hover:bg-mono-800 tracking-wider"
                >FIT</button>
              </div>
            </div>

            <div className="text-[10px] text-mono-500 mb-2">
              {wellsWithQ.length} of {wells.length} wells contributing | total {totalDerivedGpm.toFixed(0)} GPM | {pumpHrs}hr/day |
              Max combined drawdown: {wfMaxDd.toFixed(1)}ft | Transect: {span.toFixed(0)}ft
            </div>
            <p className="text-[10px] text-mono-500 mb-2">
              Drag left/right to rotate the transect bearing. Scroll to zoom. Middle-click drag to pan.
            </p>

            {/* Interactive viewport — pan/zoom transforms applied to the SVG */}
            <div
              ref={wellfieldContainerRef}
              className="relative overflow-hidden rounded bg-mono-950"
              style={{
                height: SVG_H,
                cursor: wfIsRotating ? 'ew-resize' : wfIsPanning ? 'grabbing' : 'ew-resize',
                userSelect: 'none',
              }}
              onWheel={wfHandleWheel}
              onPointerDown={wfHandlePointerDown}
              onPointerMove={wfHandlePointerMove}
              onPointerUp={wfHandlePointerUp}
              onPointerCancel={wfHandlePointerUp}
              onContextMenu={e => e.preventDefault()}
            >
              <svg
                viewBox={`0 0 ${SVG_W} ${SVG_H}`}
                width={SVG_W}
                height={SVG_H}
                style={{
                  transform: `translate(${wfPanX}px, ${wfPanY}px) scale(${wfZoom})`,
                  transformOrigin: '0 0',
                  transition: wfIsPanning || wfIsRotating ? 'none' : 'transform 0.15s ease-out',
                }}
              >
                {/* Axis lines */}
                <line x1={MARGIN.left} y1={MARGIN.top} x2={MARGIN.left} y2={SVG_H - MARGIN.bottom} stroke="#555" />
                <line x1={MARGIN.left} y1={SVG_H - MARGIN.bottom} x2={SVG_W - MARGIN.right} y2={SVG_H - MARGIN.bottom} stroke="#555" />
                <text x={SVG_W / 2} y={SVG_H - 5} fill="#666" fontSize="10" textAnchor="middle">
                  Distance along transect (ft)
                </text>
                <text x={15} y={SVG_H / 2} fill="#666" fontSize="10" textAnchor="middle"
                      transform={`rotate(-90, 15, ${SVG_H / 2})`}>
                  Combined drawdown (ft)
                </text>

                {/* Y axis ticks */}
                {yTicks.map(v => {
                  const y = pixelY(v);
                  return (
                    <g key={`y${v}`}>
                      <line x1={MARGIN.left - 5} y1={y} x2={MARGIN.left} y2={y} stroke="#555" />
                      <text x={MARGIN.left - 8} y={y + 3} fill="#888" fontSize="9" textAnchor="end">
                        {v.toFixed(v >= 10 ? 0 : v >= 1 ? 1 : 2)}
                      </text>
                      <line x1={MARGIN.left} y1={y} x2={SVG_W - MARGIN.right} y2={y}
                            stroke="#333" strokeDasharray="3 3" />
                    </g>
                  );
                })}

                {/* X axis ticks */}
                {xTicks.map(v => {
                  const x = pixelX(v);
                  if (x < MARGIN.left || x > SVG_W - MARGIN.right) return null;
                  return (
                    <g key={`x${v}`}>
                      <line x1={x} y1={SVG_H - MARGIN.bottom} x2={x} y2={SVG_H - MARGIN.bottom + 5} stroke="#555" />
                      <text x={x} y={SVG_H - MARGIN.bottom + 15} fill="#888" fontSize="9" textAnchor="middle">
                        {Math.round(v).toLocaleString()}
                      </text>
                    </g>
                  );
                })}

                {/* Saturated aquifer — solid blue body below the water table.
                    Same treatment as the single-well chart. */}
                <rect
                  x={MARGIN.left}
                  y={pixelY(0)}
                  width={plotW}
                  height={SVG_H - MARGIN.bottom - pixelY(0)}
                  fill="#06b6d4"
                  fillOpacity={0.55}
                />

                {/* Combined depression — carved out of the water body. */}
                <path d={fillPath} fill="#0a0a0a" />

                {/* Curve outline */}
                <path d={linePath} stroke="#22d3ee" fill="none" strokeWidth={2} />

                {/* Water table */}
                <line x1={MARGIN.left} y1={pixelY(0)} x2={SVG_W - MARGIN.right} y2={pixelY(0)}
                      stroke="#60a5fa" strokeWidth={2} />
                <text x={MARGIN.left + 5} y={pixelY(0) - 5} fill="#60a5fa" fontSize="9">Water Table</text>

                {/* Well casings — vertical lines at each well's transect position */}
                {projected.map((w, i) => {
                  const x = pixelX(w.pos);
                  if (x < MARGIN.left || x > SVG_W - MARGIN.right) return null;
                  return (
                    <g key={i}>
                      <line x1={x} y1={MARGIN.top} x2={x} y2={SVG_H - MARGIN.bottom}
                            stroke="#e5e7eb" strokeWidth={1.5} opacity={0.85} />
                      <text x={x} y={MARGIN.top - 5} fill="#e5e7eb" fontSize="9" textAnchor="middle">
                        {w.well_name}
                      </text>
                    </g>
                  );
                })}

                {/* Theis annotation box */}
                <g>
                  <rect x={MARGIN.left + 8} y={MARGIN.top + 8} width={150} height={42}
                        fill="#0a0a0a" stroke="#444" strokeWidth={0.5} opacity={0.85} />
                  <text x={MARGIN.left + 14} y={MARGIN.top + 22} fill="#bbb" fontSize="9">Theis (1935) · Superposed</text>
                  <text x={MARGIN.left + 14} y={MARGIN.top + 34} fill="#888" fontSize="8">
                    K = {K} ft/d · S = {S}
                  </text>
                  <text x={MARGIN.left + 14} y={MARGIN.top + 44} fill="#888" fontSize="8">
                    t = {t_days.toFixed(2)} d · {wells.length} wells
                  </text>
                </g>
              </svg>

              {/* Minimap when zoomed in — matches DV CrossSectionChart */}
              {wfZoom > 1.1 && (
                <div className="absolute bottom-2 right-2 rounded border border-mono-700 overflow-hidden bg-black/60 pointer-events-none"
                     style={{ width: 120, height: Math.round(120 * (SVG_H / SVG_W)) }}>
                  <div className="absolute border border-cyan-500" style={{
                    left: `${Math.max(0, (-wfPanX / (SVG_W * wfZoom)) * 100)}%`,
                    top: `${Math.max(0, (-wfPanY / (SVG_H * wfZoom)) * 100)}%`,
                    width: `${Math.min(100, (1 / wfZoom) * 100)}%`,
                    height: `${Math.min(100, (1 / wfZoom) * 100)}%`,
                  }} />
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* ── Top-Down Drawdown Map with ROI Circles ── */}
      <div className="bg-mono-900 border border-mono-800 rounded-lg p-4">
        <h3 className="text-sm font-semibold text-mono-200 uppercase tracking-wider mb-3">
          Drawdown Map — Radius of Influence
        </h3>
        <div className="text-[10px] text-mono-500 mb-2">
          Circles show the radius of influence (ROI) where drawdown exceeds 0.1ft. Overlapping circles indicate well interference.
        </div>
        <div className="rounded-lg overflow-hidden" style={{ height: 400 }}>
          <MapContainer center={[centerLat, centerLon]} zoom={14} style={{ height: '100%', width: '100%' }} scrollWheelZoom>
            <EsriVectorBasemap />
            {wellROIs.map((w, i) => (
              <React.Fragment key={i}>
                {/* ROI circle */}
                <Circle
                  center={[w.latitude, w.longitude]}
                  radius={w.roi_m}
                  pathOptions={{ color: '#06b6d4', fillColor: '#06b6d4', fillOpacity: 0.08, weight: 1.5, dashArray: '6 4' }}
                />
                {/* Well marker */}
                <CircleMarker
                  center={[w.latitude, w.longitude]}
                  radius={6}
                  pathOptions={{ color: '#fff', fillColor: '#06b6d4', fillOpacity: 0.9, weight: 2 }}
                >
                  <LTooltip direction="top" offset={[0, -8]} permanent={wells.length <= 15}>
                    <span style={{ fontSize: 10 }}>
                      {w.well_name} | ROI: {w.roi_ft.toLocaleString()}ft | {w.pump_rate_gpm.toFixed(0)} GPM {w.q_source === 'derived' ? '(derived)' : w.q_source === 'manual' ? '(manual)' : w.q_source === 'manual_use_case' ? '(use case)' : ''}
                    </span>
                  </LTooltip>
                </CircleMarker>
              </React.Fragment>
            ))}
          </MapContainer>
        </div>
      </div>

      {/* ── Well Interference Zones ── */}
      <div className="bg-mono-900 border border-mono-800 rounded-lg p-4">
        <h3 className="text-xs font-semibold text-mono-300 uppercase tracking-wider mb-1">
          Well Interference Zones
        </h3>
        <div className="text-[10px] text-mono-500 mb-3">
          Areas where multiple well ROIs overlap. Yellow = 2 wells share that
          area, red = 3+ wells share it. Non-overlapping ROI area is not shaded.
        </div>
        <div style={{ height: 400 }}>
          <MapContainer
            center={[centerLat, centerLon]}
            zoom={13}
            style={{ height: '100%', width: '100%' }}
            scrollWheelZoom={true}
          >
            <EsriVectorBasemap />
            {/* Overlap-count grid. Pure geometry — for each cell, count how
                many well ROIs contain it. Only render cells with count >= 2. */}
            {(() => {
              const cosLat = Math.cos(centerLat * Math.PI / 180);
              const gridRes = 0.0008; // ~80 m cells for clean overlap edges
              // Dynamic extent: wrap every ROI with a little padding so we
              // never clip an interference zone that lies on the edge of the
              // map's default 3 km window.
              const maxRoiFt = Math.max(...wellROIs.map(w => w.roi_ft), 0);
              const maxRoiDeg = maxRoiFt / 364000; // ~1 ft per 0.0000027° lat
              const extent = Math.max(0.025, maxRoiDeg * 1.4);
              const cells: { lat: number; lon: number; count: number }[] = [];

              for (let lat = centerLat - extent; lat <= centerLat + extent; lat += gridRes) {
                for (let lon = centerLon - extent; lon <= centerLon + extent; lon += gridRes) {
                  let count = 0;
                  for (const w of wellROIs) {
                    const dx = (lon - w.longitude) * 308000 * cosLat;
                    const dy = (lat - w.latitude) * 364000;
                    const dist = Math.sqrt(dx * dx + dy * dy);
                    if (dist <= w.roi_ft) count++;
                  }
                  if (count >= 2) cells.push({ lat, lon, count });
                }
              }

              return cells.map((c, i) => {
                const isHeavy = c.count >= 3;
                const fillCol = isHeavy ? '#ef4444' : '#facc15';
                return (
                  <Rectangle
                    key={i}
                    bounds={[
                      [c.lat - gridRes / 2, c.lon - gridRes / 2],
                      [c.lat + gridRes / 2, c.lon + gridRes / 2],
                    ]}
                    pathOptions={{
                      // Match stroke to fill so adjacent cells blend into a
                      // continuous block instead of showing seams between them.
                      color: fillCol,
                      fillColor: fillCol,
                      fillOpacity: isHeavy ? 0.85 : 0.8,
                      opacity: isHeavy ? 0.85 : 0.8,
                      weight: 0.5,
                    }}
                  />
                );
              });
            })()}
            {/* Well markers */}
            {wells.map((w, i) => (
              <CircleMarker key={i}
                center={[w.latitude, w.longitude]}
                radius={6}
                pathOptions={{ color: '#fff', fillColor: '#06b6d4', fillOpacity: 0.9, weight: 2 }}
              />
            ))}
          </MapContainer>
        </div>
        <div className="flex items-center gap-4 mt-2 text-[10px] text-mono-500">
          <span className="flex items-center gap-1">
            <span className="w-3 h-3 rounded" style={{ backgroundColor: '#facc15', opacity: 0.85 }} />
            2-well overlap
          </span>
          <span className="flex items-center gap-1">
            <span className="w-3 h-3 rounded" style={{ backgroundColor: '#ef4444', opacity: 0.9 }} />
            3+ well overlap
          </span>
          <span className="text-mono-600 ml-2">overlap counted against each well's individual ROI</span>
        </div>
      </div>
    </div>
  );
}
